import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Switch, Button, Row, Col, Card, Space, Tag, message, Tabs, Tooltip, Divider } from "antd";
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, CodeOutlined, EyeOutlined } from "@ant-design/icons";
import { emailTemplateService } from "../../../api/enhancedApi";
import { EMAIL_TEMPLATE_CONSTANTS } from "../../../api/emailTemplateApi";
import { processHtmlWithSampleData } from "./utils/contentBlockUtils";
import { airportApi } from "../../../api/airportApi";
import { masterDataApi } from "../../../api/masterDataApi";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const EmailTemplateEditor = ({ visible, template, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("basic");
    const [tags, setTags] = useState([]);
    const [newTag, setNewTag] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");

    // 机场和供应商数据
    const [airports, setAirports] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loadingAirports, setLoadingAirports] = useState(false);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);

    // 加载机场数据
    const loadAirports = async () => {
        try {
            setLoadingAirports(true);
            const response = await airportApi.getAirports();
            setAirports(response.data || []);
        } catch (error) {
            console.error("加载机场数据失败:", error);
            message.error("加载机场数据失败");
        } finally {
            setLoadingAirports(false);
        }
    };

    // 加载供应商数据
    const loadSuppliers = async () => {
        try {
            setLoadingSuppliers(true);
            const response = await masterDataApi.getSuppliers();
            setSuppliers(response.data || []);
        } catch (error) {
            console.error("加载供应商数据失败:", error);
            message.error("加载供应商数据失败");
        } finally {
            setLoadingSuppliers(false);
        }
    };

    useEffect(() => {
        if (visible) {
            // 加载基础数据
            loadAirports();
            loadSuppliers();

            if (template) {
                // 编辑模式
                form.setFieldsValue({
                    template_code: template.template_code,
                    name: template.name,
                    description: template.description,
                    category: template.category,
                    subject_template: template.subject_template,
                    body_html: template.body_html,
                    body_text: template.body_text,
                    status: template.status,
                    is_public: template.is_public,
                    applicable_airports: template.applicable_airports || [],
                    applicable_suppliers: template.applicable_suppliers || [],
                });
                setTags(template.tags || []);
                setPreviewHtml(template.body_html || "");
            } else {
                // 新建模式
                form.resetFields();
                form.setFieldsValue({
                    status: "DRAFT",
                    is_public: true,
                    category: "GENERAL",
                    applicable_airports: [],
                    applicable_suppliers: [],
                });
                setTags([]);
                setPreviewHtml("");
            }
        }
    }, [visible, template, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const templateData = {
                ...values,
                tags: tags.length > 0 ? tags : null,
            };

            if (template) {
                // 更新模板
                await emailTemplateService.updateTemplate(template.id, templateData);
                message.success("模板更新成功");
            } else {
                // 创建模板
                await emailTemplateService.createTemplate(templateData);
                message.success("模板创建成功");
            }

            onSuccess();
        } catch (error) {
            if (error.errorFields) {
                // 表单验证错误
                return;
            }
            message.error(template ? "更新模板失败" : "创建模板失败");
            console.error("Submit template error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddTag = () => {
        if (newTag && !tags.includes(newTag)) {
            setTags([...tags, newTag]);
            setNewTag("");
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setTags(tags.filter((tag) => tag !== tagToRemove));
    };

    const handleHtmlChange = (value) => {
        setPreviewHtml(value);
    };

    const insertContentBlock = (blockType) => {
        const currentHtml = form.getFieldValue("body_html") || "";
        const blockHtml = `<div data-content-block-type="${blockType}" data-content-block-label="点击右侧插入${getContentBlockLabel(blockType)}"></div>`;
        const newHtml = currentHtml + "\n" + blockHtml;

        form.setFieldsValue({ body_html: newHtml });
        setPreviewHtml(newHtml);
    };

    const getContentBlockLabel = (blockType) => {
        const labels = {
            flightInfo: "航班信息",
            attachment: "附件",
            contact: "联系人",
            cargoManifest: "货物舱单",
            crewInfo: "机组信息",
            fuelInfo: "加油信息",
            cateringInfo: "餐食信息",
            hotacInfo: "酒店信息",
            permitInfo: "许可信息",
        };
        return labels[blockType] || blockType;
    };

    const getCategoryText = (category) => {
        const categoryMap = {
            FLIGHT_OPERATION: "航班运行",
            FUEL_REQUEST: "加油申请",
            CATERING_ORDER: "餐食订单",
            CREW_TRANSPORTATION: "机组交通",
            HOTAC: "酒店住宿",
            PERMIT_REQUEST: "许可申请",
            GENERAL: "通用",
        };
        return categoryMap[category] || category;
    };

    const contentBlockButtons = [
        { type: "flightInfo", label: "航班信息", color: "blue" },
        { type: "attachment", label: "附件", color: "green" },
        { type: "contact", label: "联系人", color: "orange" },
        { type: "cargoManifest", label: "货物舱单", color: "purple" },
        { type: "crewInfo", label: "机组信息", color: "cyan" },
        { type: "fuelInfo", label: "加油信息", color: "red" },
        { type: "cateringInfo", label: "餐食信息", color: "lime" },
        { type: "hotacInfo", label: "酒店信息", color: "gold" },
        { type: "permitInfo", label: "许可信息", color: "magenta" },
    ];

    return (
        <Modal
            title={template ? "编辑邮件模板" : "新建邮件模板"}
            open={visible}
            onCancel={onCancel}
            width={1200}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    {template ? "更新" : "创建"}
                </Button>,
            ]}
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    status: "DRAFT",
                    is_public: true,
                    category: "GENERAL",
                }}
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab="基本信息" key="basic">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="template_code"
                                    label="模板代码"
                                    rules={[
                                        { required: true, message: "请输入模板代码" },
                                        { pattern: /^[A-Z0-9_]+$/, message: "模板代码只能包含大写字母、数字和下划线" },
                                    ]}
                                    extra="用于程序化引用，建议使用大写字母和下划线"
                                >
                                    <Input placeholder="例如: FLIGHT_OPERATION_NOTICE" disabled={!!template} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="name" label="模板名称" rules={[{ required: true, message: "请输入模板名称" }]}>
                                    <Input placeholder="例如: 航班运行通知模板" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="category" label="模板分类" rules={[{ required: true, message: "请选择模板分类" }]}>
                                    <Select placeholder="选择分类">
                                        {Object.entries(EMAIL_TEMPLATE_CONSTANTS.CATEGORY).map(([key, value]) => (
                                            <Option key={value} value={value}>
                                                {getCategoryText(value)}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="status" label="模板状态" rules={[{ required: true, message: "请选择模板状态" }]}>
                                    <Select>
                                        <Option value="DRAFT">草稿</Option>
                                        <Option value="ACTIVE">启用</Option>
                                        <Option value="INACTIVE">禁用</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="is_public" label="公共模板" valuePropName="checked">
                                    <Switch checkedChildren="是" unCheckedChildren="否" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="description" label="模板描述">
                            <TextArea rows={3} placeholder="描述模板的用途和使用场景" />
                        </Form.Item>

                        <Form.Item name="subject_template" label="邮件主题模板" rules={[{ required: true, message: "请输入邮件主题模板" }]} extra="可以使用变量，例如: {{flight_number}} 航班运行通知">
                            <Input placeholder="例如: {{flight_number}} 航班运行通知" />
                        </Form.Item>

                        {/* 适配机场和供应商 */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="applicable_airports"
                                    label="适配机场"
                                    extra="选择此模板适用的机场，留空表示适用于所有机场"
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="选择适配的机场"
                                        loading={loadingAirports}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {airports.map((airport) => (
                                            <Option key={airport.iata_code} value={airport.iata_code}>
                                                {airport.iata_code} - {airport.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="applicable_suppliers"
                                    label="适配供应商"
                                    extra="选择此模板适用的供应商，留空表示适用于所有供应商"
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="选择适配的供应商"
                                        loading={loadingSuppliers}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {suppliers.map((supplier) => (
                                            <Option key={supplier.id} value={supplier.id}>
                                                {supplier.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* 标签管理 */}
                        <Form.Item label="标签">
                            <Space wrap>
                                {tags.map((tag) => (
                                    <Tag key={tag} closable onClose={() => handleRemoveTag(tag)}>
                                        {tag}
                                    </Tag>
                                ))}
                                <Input type="text" size="small" style={{ width: 100 }} value={newTag} onChange={(e) => setNewTag(e.target.value)} onPressEnter={handleAddTag} placeholder="新标签" />
                                <Button size="small" icon={<PlusOutlined />} onClick={handleAddTag}>
                                    添加
                                </Button>
                            </Space>
                        </Form.Item>
                    </TabPane>

                    <TabPane tab="邮件内容" key="content">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Card
                                    title="HTML 内容编辑"
                                    size="small"
                                    extra={
                                        <Tooltip title="支持HTML标签和内容块">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    }
                                >
                                    <Form.Item name="body_html" rules={[{ required: true, message: "请输入HTML格式的邮件内容" }]}>
                                        <TextArea rows={20} placeholder="输入HTML格式的邮件内容..." onChange={(e) => handleHtmlChange(e.target.value)} />
                                    </Form.Item>

                                    <Divider orientation="left" plain>
                                        快速插入内容块
                                    </Divider>
                                    <Space wrap>
                                        {contentBlockButtons.map((block) => (
                                            <Button key={block.type} size="small" onClick={() => insertContentBlock(block.type)}>
                                                <Tag color={block.color}>{block.label}</Tag>
                                            </Button>
                                        ))}
                                    </Space>
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card title="预览" size="small" extra={<EyeOutlined />}>
                                    <div
                                        style={{
                                            height: "400px",
                                            overflow: "auto",
                                            border: "1px solid #d9d9d9",
                                            padding: "12px",
                                            backgroundColor: "#fafafa",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: processHtmlWithSampleData(previewHtml) }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Form.Item name="body_text" label="纯文本内容（可选）" extra="用于不支持HTML的邮件客户端">
                            <TextArea rows={6} placeholder="输入纯文本格式的邮件内容..." />
                        </Form.Item>
                    </TabPane>
                </Tabs>
            </Form>
        </Modal>
    );
};

export default EmailTemplateEditor;
